import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { useSystemMetrics } from '../hooks/useSystemMetrics';
import { usePerformanceMetrics } from '../hooks/usePerformanceMetrics';
import { useServiceStatus } from '../hooks/useServiceStatus';
import { useDatabaseStatus } from '../hooks/useDatabaseStatus';
import { useThemeContext } from '../contexts/ThemeContext';
import ExtensionWorkersPanel from '../components/ExtensionWorkersPanel';

const SystemStatus: React.FC = () => {
  const { isDarkMode } = useThemeContext();
  const { data: systemStatus, isLoading } = useSystemStatus();
  const { data: systemMetrics, isLoading: metricsLoading } = useSystemMetrics();
  const { data: performanceMetrics, isLoading: performanceLoading } = usePerformanceMetrics();
  const { data: serviceStatus, isLoading: serviceLoading } = useServiceStatus();
  const { data: databaseStatus, isLoading: dbLoading } = useDatabaseStatus();

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'running':
      case 'connected':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
      case 'error':
      case 'stopped':
      case 'disconnected':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'running':
      case 'connected':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'critical':
      case 'error':
      case 'stopped':
      case 'disconnected':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const getMetricStatus = (name: string, value: number): 'healthy' | 'warning' | 'critical' => {
    switch (name.toLowerCase()) {
      case 'cpu usage':
        if (value < 70) return 'healthy';
        if (value < 90) return 'warning';
        return 'critical';
      case 'memory usage':
        if (value < 80) return 'healthy';
        if (value < 95) return 'warning';
        return 'critical';
      case 'disk usage':
        if (value < 80) return 'healthy';
        if (value < 95) return 'warning';
        return 'critical';
      case 'network latency':
        if (value < 50) return 'healthy';
        if (value < 200) return 'warning';
        return 'critical';
      default:
        return 'healthy';
    }
  };

  return (
    <Box>
      {/* System Health Overview - Left Side */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Overall Health
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isLoading ? <CircularProgress size={24} /> : getStatusIcon('healthy')}
                    <Typography variant="h4" color="success">
                      {isLoading ? 'Loading...' : 'Healthy'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {isLoading ? 'Checking system status...' : 'All systems operational'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Services
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {serviceLoading ? '...' : serviceStatus?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    of {serviceLoading ? '...' : serviceStatus?.length || 0} services
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    System Load
                  </Typography>
                  <Typography variant="h4" color="warning">
                    {metricsLoading
                      ? '...'
                      : (systemMetrics?.cpu_usage ?? 0).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    CPU utilization
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Memory Usage
                  </Typography>
                  <Typography variant="h4" color="warning">
                    {metricsLoading
                      ? '...'
                      : (systemMetrics?.memory_usage ?? 0).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    RAM utilization
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Disk Usage
                  </Typography>
                  <Typography variant="h4" color="info">
                    {metricsLoading
                      ? '...'
                      : (systemMetrics?.disk_usage ?? 0).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Storage utilization
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Network Latency
                  </Typography>
                  <Typography variant="h4" color="secondary">
                    {metricsLoading ? '...' : systemMetrics?.network_latency || '0'}
                    ms
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Response time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Side - Additional Metrics */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Task Queue
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {performanceLoading ? '...' : performanceMetrics?.scraping?.active_workers || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active workers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Request Rate & Load
                  </Typography>
                  <Typography variant="h4" color="warning">
                    {performanceLoading
                      ? '...'
                      : performanceMetrics?.scraping?.requests_per_minute || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Requests per minute • Success:{' '}
                    {performanceLoading
                      ? '...'
                      : performanceMetrics?.scraping?.success_rate?.toFixed(1) || 0}
                    %
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* System Metrics */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Metrics
            {metricsLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          {systemMetrics && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      CPU Usage
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(
                        getMetricStatus('CPU Usage', (systemMetrics?.cpu_usage ?? 0))
                      )}
                      <Typography variant="body2">
                        {(systemMetrics?.cpu_usage ?? 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(systemMetrics?.cpu_usage ?? 0)}
                    color={getStatusColor(
                      getMetricStatus('CPU Usage', (systemMetrics?.cpu_usage ?? 0))
                    )}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Memory Usage
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(
                        getMetricStatus('Memory Usage', (systemMetrics?.memory_usage ?? 0))
                      )}
                      <Typography variant="body2">
                        {(systemMetrics?.memory_usage ?? 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(systemMetrics?.memory_usage ?? 0)}
                    color={getStatusColor(
                      getMetricStatus('Memory Usage', (systemMetrics?.memory_usage ?? 0))
                    )}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Disk Usage
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(
                        getMetricStatus('Disk Usage', (systemMetrics?.disk_usage ?? 0))
                      )}
                      <Typography variant="body2">
                        {(systemMetrics?.disk_usage ?? 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(systemMetrics?.disk_usage ?? 0)}
                    color={getStatusColor(
                      getMetricStatus('Disk Usage', (systemMetrics?.disk_usage ?? 0))
                    )}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Network Latency
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(
                        getMetricStatus('Network Latency', (systemMetrics?.network_latency ?? 0))
                      )}
                      <Typography variant="body2">{(systemMetrics?.network_latency ?? 0)}ms</Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(((systemMetrics?.network_latency ?? 0) / 2), 100)}
                    color={getStatusColor(
                      getMetricStatus('Network Latency', (systemMetrics?.network_latency ?? 0))
                    )}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Service Status */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Service Status
            {serviceLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          {serviceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : serviceStatus && serviceStatus.length > 0 ? (
            <Grid container spacing={2}>
              {serviceStatus.map(service => (
                <Grid item xs={12} md={6} key={service.name}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: isDarkMode ? 'grey.600' : 'grey.200',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {service.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Uptime: {service.uptime}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Last check: {service.last_check}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Response: {service.response_time}ms
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(service.status)}
                      <Chip
                        label={service.status}
                        size="small"
                        color={getStatusColor(service.status)}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography color="textSecondary">No service status available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Database Status */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Database Status
            {dbLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          {/* databaseStatus is removed, so this will always be empty */}
          {dbLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : databaseStatus && databaseStatus.length > 0 ? (
            <Grid container spacing={2}>
              {databaseStatus.map(db => (
                <Grid item xs={12} md={6} key={db.name}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: isDarkMode ? 'grey.600' : 'grey.200',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      {db.name}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        Status:
                      </Typography>
                      <Chip label={db.status} size="small" color={getStatusColor(db.status)} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        Connections:
                      </Typography>
                      <Typography variant="caption">{db.connections}</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        Size:
                      </Typography>
                      <Typography variant="caption">
                        {db.size_mb?.toFixed(2) || '0.00'} MB
                      </Typography>
                    </Box>
                    {db.last_backup && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Last Backup:
                        </Typography>
                        <Typography variant="caption">{db.last_backup}</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography color="textSecondary">No database status available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <ExtensionWorkersPanel />

      {/* Performance Metrics */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Performance Metrics
            {performanceLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          {performanceMetrics && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Scraping Performance
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Requests per minute: {performanceMetrics.scraping.requests_per_minute}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Success rate: {(performanceMetrics.scraping.success_rate * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active workers: {performanceMetrics.scraping.active_workers}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Database Performance
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Queries per second: {performanceMetrics.database.queries_per_second}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Connection pool usage:{' '}
                    {(performanceMetrics.database.connection_pool_usage * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Last Updated */}
      {systemStatus && (
        <Box
          sx={{
            textAlign: 'center',
            mt: 4,
            p: 2,
            bgcolor: isDarkMode ? 'grey.800' : 'grey.50',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Last updated: {new Date(systemStatus.last_updated).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Data refreshes every 5 seconds
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SystemStatus;
